import {algoliasearch} from 'algoliasearch';
import instantsearch from 'instantsearch.js';
import {
    clearRefinements,
    configure,
    hierarchicalMenu,
    refinementList,
} from 'instantsearch.js/es/widgets';
import {history} from 'instantsearch.js/es/lib/routers';
import {
    algoliaAppId,
    algoliaApiKey,
    depositaryMapSearchIndex,
} from '../variables';
import {
    dynamicUriTemplate,
    extractParamsFromUri,
    toggleLockBody,
} from '../functions';
import {
    connectCurrentRefinements,
    connectSearchBox,
    connectStats,
} from 'instantsearch.js/es/connectors';
import {MarkerClusterer} from '@googlemaps/markerclusterer';

const {AdvancedMarkerElement} =
    await google.maps.importLibrary('marker');

const object = vnObject;
const {__, _n, sprintf} = wp.i18n;

let networkSearch;

document.addEventListener('DOMContentLoaded', async (dom) => {
    const {network, place_type} = object.algoliaQueryStrings;
    const networkMainSlug = object.networkMainSlug;
    const slugs = {
        network,
        place_type,
    };

    const networkPage = dom.target.querySelector('.js-network-link');

    if (!networkPage) {
        return;
    }

    function buildFacetFiltersFromHelperState(helper) {
        const facetFilters = [];


        Object.entries(helper.state.disjunctiveFacetsRefinements).forEach(
            ([attribute, values]) => {
                if (values && values.length) {
                    facetFilters.push(
                        values.map((v) => `${attribute}:${v}`)
                    );
                }
            }
        );

        Object.entries(helper.state.facetsRefinements).forEach(
            ([attribute, values]) => {
                if (values && values.length) {
                    facetFilters.push(
                        values.map((v) => `${attribute}:${v}`)
                    );
                }
            }
        );

        const {hierarchicalFacets, hierarchicalFacetsRefinements} = helper.state;

        hierarchicalFacets.forEach((facet) => {
            const facetName = facet.name;
            const refinements = hierarchicalFacetsRefinements[facetName];

            if (!refinements || !refinements.length) {
                return;
            }

            const separator = facet.separator || ' > ';
            const path = refinements[0];
            const parts = path.split(separator);

            const attributes = facet.attributes || [];


            for (
                let level = 0;
                level < parts.length && level < attributes.length;
                level++
            ) {
                const attribute = attributes[level];
                const value = parts.slice(0, level + 1).join(separator);

                facetFilters.push(`${attribute}:${value}`);
            }

        });

        return facetFilters;
    }

    const searchClient = algoliasearch(algoliaAppId, algoliaApiKey, {});


    const results = [];

    networkSearch = instantsearch({
        indexName: depositaryMapSearchIndex,
        searchClient: searchClient,
        numberLocale: "fr",
        insights: true,
        future: {
            preserveSharedStateOnUnmount: true,
        },
        routing: {
            router: history({
                cleanUrlOnDispose: false,
                createURL({location, routeState = {}}) {
                    const params = {
                        network: routeState.network,
                        place_type: routeState.place_type,
                    };

                    return dynamicUriTemplate(location.origin, params, networkMainSlug, slugs);
                },

                parseURL({location}) {
                    const params =
                        extractParamsFromUri(location.href, slugs) || {};

                    return {
                        network: params[slugs.network],
                        place_type: params[slugs.place_type],
                    };
                },
            }),

            stateMapping: {
                stateToRoute(uiState) {
                    const indexUiState = uiState[depositaryMapSearchIndex] || {};
                    return {
                        network: indexUiState.hierarchicalMenu?.['network.lvl0'],
                        place_type: indexUiState.place_type?.['name'],
                    };
                },

                routeToState(routeState = {}) {
                    return {
                        [depositaryMapSearchIndex]: {
                            hierarchicalMenu: {
                                'network.lvl0': routeState.network,
                            },
                        },
                    };
                },
            },
        },
        onStateChange({uiState, setUiState}) {
            setUiState(uiState);
        },
    });

    const renderSearchBox = (renderOptions, isFirstRender) => {
        const {query, refine, clear, isSearchStalled, widgetParams} =
            renderOptions;
        const container = document.querySelector(widgetParams.container);

        if (isFirstRender) {
            const reset = document.createElement('button');
            reset.type = 'reset';
            reset.insertAdjacentHTML(
                'beforeend',
                '<i class="vn-icons-close"></i>',
            );

            reset.addEventListener('click', () => {
                clear();
            });

            container.appendChild(reset);
        }

        container.querySelector('button[type="reset"]').hidden =
            isSearchStalled || !query;
    };

    const customSearchBox = connectSearchBox(renderSearchBox);

    const renderStats = (renderOptions) => {
        const {nbHits, instantSearchInstance} = renderOptions;
        let numberOfFilters = 0;

        if (instantSearchInstance.helper) {
            const helperState = instantSearchInstance.helper.state;

            const disjunctive = helperState.disjunctiveFacetsRefinements;
            Object.keys(disjunctive).forEach((facet) => {
                numberOfFilters += disjunctive[facet].length;
            });

            const hierarchical = helperState.hierarchicalFacetsRefinements;
            Object.keys(hierarchical).forEach((facet) => {
                numberOfFilters += hierarchical[facet].length;
            });
        }

        const filterNode = dom.target.querySelector('.js-network-filter');
        if (filterNode) {
            filterNode.textContent = `(${numberOfFilters})`;
        }

        const statNode = dom.target.querySelector('.js-network-stat');
        if (statNode) {
            statNode.innerHTML = `<p>${nbHits} ${__(
                'results displayed',
                'vn',
            )}</p>`;
        }
    };

    const customStats = connectStats(renderStats);

    const renderCurrentRefinements = ({items, widgetParams}) => {
        const container = dom.target.querySelector(widgetParams.container);
        const totalItemCount = items.reduce(
            (acc, curr) => acc + curr.refinements.length,
            0,
        );

        container.innerHTML = sprintf(
            _n(
                '%s remove the filter',
                '%s remove filters',
                totalItemCount,
                'vn',
            ),
            totalItemCount,
        );
    };

    const customCurrentRefinements = connectCurrentRefinements(
        renderCurrentRefinements,
    );

    // --- PERF: templates DOM (créés une seule fois) ---
    const singleMarkerTemplate = (() => {
        const wrapper = document.createElement('div');
        wrapper.className = 'vn-marker-single';
        wrapper.innerHTML =
            `<img class="vn-marker-single-img" src="/wp-content/themes/wp_groupe_totem/src/svg/marker-single.svg" alt="">`;
        return wrapper;
    })();

    function createSingleMarkerContent() {
        // PERF: clone (au lieu de recréer/inline-styles à chaque marker)
        return singleMarkerTemplate.cloneNode(true);
    }

    function createClusterContent(count) {
        // PERF: DOM plus simple, styles via CSS si possible (mais ok même sans)
        const container = document.createElement('div');
        container.className = 'vn-marker-cluster';
        container.innerHTML = `
      <img class="vn-marker-cluster-icon" src="/wp-content/themes/wp_groupe_totem/src/svg/marker.svg" alt="">
      <div class="vn-marker-label">${String(count)}</div>
    `;
        return container;
    }

    function createMapWidget(widgetParams) {
        let map = null;

        // PERF: cache de markers + clusterer persistant
        let clusterer = null;
        const markerById = new Map(); // objectID -> AdvancedMarkerElement

        // garde ton anti-race condition
        let lastRequestId = 0;

        // PERF: optionnel mais très utile pour éviter resync si mêmes ids (InstantSearch render souvent)
        let lastIdsKey = '';

        const computeIdsKey = (hits) => {
            // tri pour stabilité si l’ordre des hits varie
            const ids = [];
            for (const h of hits) if (h && h.objectID) ids.push(h.objectID);
            ids.sort();
            return ids.join('|');
        };

        const syncMarkers = (hits) => {
            // On ne touche pas à ta logique hits -> markers, on l’optimise juste
            const nextIds = new Set();
            const toAdd = [];
            const toRemove = [];

            for (const item of hits) {
                if (
                    !item ||
                    !item.objectID ||
                    !item._geoloc ||
                    typeof item._geoloc.lat !== 'number' ||
                    typeof item._geoloc.lng !== 'number'
                ) {
                    continue;
                }

                const id = item.objectID;
                nextIds.add(id);

                let marker = markerById.get(id);
                if (!marker) {
                    marker = new AdvancedMarkerElement({
                        map,
                        position: { lat: item._geoloc.lat, lng: item._geoloc.lng },
                        content: createSingleMarkerContent(),
                    });
                    markerById.set(id, marker);
                    toAdd.push(marker);
                } else {
                    // Si tes points peuvent bouger, on met à jour la position.
                    // (Sinon, tu peux enlever ce bloc.)
                    const pos = marker.position;
                    const lat = item._geoloc.lat;
                    const lng = item._geoloc.lng;
                    // marker.position est un LatLng|LatLngLiteral selon API; on fait safe.
                    const curLat = typeof pos?.lat === 'function' ? pos.lat() : pos?.lat;
                    const curLng = typeof pos?.lng === 'function' ? pos.lng() : pos?.lng;
                    if (curLat !== lat || curLng !== lng) {
                        marker.position = { lat, lng };
                    }
                }
            }

            // remove seulement ceux qui disparaissent
            for (const [id, marker] of markerById) {
                if (!nextIds.has(id)) {
                    markerById.delete(id);
                    marker.map = null; // retire du rendu
                    toRemove.push(marker);
                }
            }

            // clusterer: on ne le recrée jamais
            if (toRemove.length) clusterer.removeMarkers(toRemove, true);
            if (toAdd.length) clusterer.addMarkers(toAdd, true);
            clusterer.render();
        };

        const updateResultsAndMap = async (helper) => {
            const requestId = ++lastRequestId;

            const facetFilters = buildFacetFiltersFromHelperState(helper);

            const searchParams = {
                query: helper.state.query || '',
                hitsPerPage: 1000, // inchangé
            };

            if (facetFilters.length) {
                searchParams.facetFilters = facetFilters;
            }

            let hits = [];

            try {
                let page = 0;
                let finished = false;

                while (!finished) {
                    searchParams.page = page;
                    const res = await searchClient.searchSingleIndex({
                        indexName: depositaryMapSearchIndex,
                        searchParams,
                    });

                    hits.push(...res.hits);

                    finished = page >= res.nbPages - 1;
                    page++;
                }

                if (requestId !== lastRequestId || !map) return;
            } catch (error) {
                if (requestId !== lastRequestId) return;
                console.error('Error in searchSingleIndex for map widget', error);
                return;
            }

            // (tu gardes ton tableau results global inchangé)
            results.length = 0;
            for (const hit of hits) results.push(hit);

            // PERF: évite de recalculer / re-render si mêmes IDs (InstantSearch rerender souvent)
            const idsKey = computeIdsKey(hits);
            if (idsKey === lastIdsKey) return;
            lastIdsKey = idsKey;

            syncMarkers(hits);
        };

        return {
            init({ helper }) {
                const container = widgetParams.container;

                map = new google.maps.Map(container, {
                    center: { lat: 48.8566, lng: 2.3522 },
                    zoom: 6,
                    mapId: object.mapApiKey,
                    styles: [
                        { featureType: "poi", stylers: [{ visibility: "off" }] },
                        { featureType: "transit", stylers: [{ visibility: "off" }] },
                    ],
                });

                map.addListener("click", (e) => {
                    if (e.placeId) e.stop();
                });

                // PERF: clusterer créé une seule fois
                clusterer = new MarkerClusterer({
                    map,
                    markers: [],
                    renderer: {
                        render({ count, position }) {
                            const content = createClusterContent(count);
                            return new window.google.maps.marker.AdvancedMarkerElement({
                                position,
                                content,
                            });
                        },
                    },
                });

                updateResultsAndMap(helper);
            },

            render({ helper }) {
                if (!map) return;
                updateResultsAndMap(helper);
            },
        };
    }


    /*function createMapWidget(widgetParams) {
        let map = null;
        let markers = [];
        let clusterer = null;

        let lastRequestId = 0;

        const updateResultsAndMap = async (helper) => {
            const requestId = ++lastRequestId;

            const facetFilters = buildFacetFiltersFromHelperState(helper);

            const searchParams = {
                query: helper.state.query || '',
                hitsPerPage: 1000,
            };

            if (facetFilters.length) {
                searchParams.facetFilters = facetFilters;
            }

            let hits = [];

            try {
                let page = 0;
                let finished = false;

                while (!finished) {
                    searchParams.page = page
                    const res = await searchClient.searchSingleIndex({
                        indexName: depositaryMapSearchIndex,
                        searchParams,
                    });

                    hits.push(...res.hits);

                    finished = page >= res.nbPages - 1;
                    page++;
                }


                if (requestId !== lastRequestId || !map) {
                    return;
                }

            } catch (error) {

                if (requestId !== lastRequestId) {
                    return;
                }
                console.error('Error in searchSingleIndex for map widget', error);
                return;
            }


            results.length = 0;
            for (const hit of hits) {
                results.push(hit);
            }


            if (clusterer) {
                clusterer.clearMarkers();
            }

            markers.forEach((marker) => {
                marker.map = null;
            });
            markers = [];


            const newMarkers = hits
                .filter(
                    (item) =>
                        item &&
                        item._geoloc &&
                        typeof item._geoloc.lat === 'number' &&
                        typeof item._geoloc.lng === 'number',
                )
                .map((item) => {
                    const position = {
                        lat: item._geoloc.lat,
                        lng: item._geoloc.lng,
                    };

                    const content = createSingleMarkerContent();

                    const marker = new AdvancedMarkerElement({
                        map,
                        position,
                        content,
                    });

                    return marker;
                });

            markers = newMarkers;


            clusterer = new MarkerClusterer({
                map,
                markers,
                renderer: {
                    render({count, position}) {
                        const content = createClusterContent(count);

                        return new window.google.maps.marker.AdvancedMarkerElement({
                            position,
                            content,
                        });
                    },
                },
            });


        };

        return {
            init({helper}) {
                const container = widgetParams.container;

                map = new google.maps.Map(container, {
                    center: {lat: 48.8566, lng: 2.3522},
                    zoom: 6,
                    mapId: object.mapApiKey,
                    styles: [
                        {featureType: "poi", stylers: [{visibility: "off"}]},
                        {featureType: "transit", stylers: [{visibility: "off"}]},
                    ],
                });

                map.addListener("click", (e) => {
                    if (e.placeId) {
                        e.stop();
                    }
                });


                updateResultsAndMap(helper);
            },

            render({helper}) {
                if (!map) return;
                updateResultsAndMap(helper);
            },
        };
    } */

    networkSearch.addWidgets([

        createMapWidget({
            container: document.querySelector('.pt-network__map-handler'),
        }),

        clearRefinements({
            container: '.pt-network__extra--reset-handler',
            templates: {
                resetLabel({hasRefinements}, {html}) {
                    return html`${hasRefinements
                        ? `<span>${__(
                            'Remove filters',
                            'vn',
                        )}<span class="js-network-filter"></span></span>`
                        : ''}`;
                },
            },
        }),

        hierarchicalMenu({
            container: '.pt-network__depositary-network-handler',
            attributes: ['network.lvl0', 'network.lvl1', 'network.lvl2'],
            limit: 9999,
        }),

        refinementList({
            container: '.pt-network__depositary-place_type-handler',
            attribute: 'place_type.name',
            limit: 9999,
        }),

        configure({
            hitsPerPage: 9999,
        }),

        customStats({}),
    ]);

    networkSearch.start();

    dom.target
        .querySelector('.js-show-mobile-modal')
        ?.addEventListener('click', () => {
            dom.target
                .querySelector('.js-network-mobile-modal')
                .classList.toggle('active');
            toggleLockBody();
        });

    dom.target
        .querySelectorAll('.js-close-mobile-modal')
        .forEach((btn) => {
            btn.addEventListener('click', () => {
                dom.target
                    .querySelector('.js-network-mobile-modal')
                    .classList.toggle('active');
                toggleLockBody();
            });
        });
});

export {networkSearch};
